'use client';

import { usePrint } from '@/lib/hooks';
import { AuthorityDetails, PageProps, SocialMedia } from '@/lib/types';
import { Breadcrumb, CustomLink, Section, SocialMediaIcon } from '@/ui';
import { Content } from '@/widgets';
import { Button, Container, Flex, Group, Table } from '@mantine/core';
import { Map, Placemark, YMaps } from '@pbe/react-yandex-maps';
import clsx from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import sanitize from 'sanitize-html';
import classnames from './contacts.module.css';

type Props = {
  contacts: PageProps & {
    data: AuthorityDetails;
  };
};

export default function Contacts({ contacts }: Props) {
  const { ref, handlePrint } = usePrint();
  const t = useTranslations();
  const locale = useLocale();

  const breadcrumbItems = [
    { title: contacts?.breadcrumb, href: `/general-menu/${contacts?.menu_id}` },
    { title: contacts?.menu_item_title, isCurrent: true },
  ];

  const websiteLink = contacts?.data?.website?.value?.includes('http')
    ? contacts?.data?.website?.value
    : `https://${contacts?.data?.website?.value}`;

  return (
    <Container>
      <Section
        rightMenu={{
          isActive: true,
          id: contacts?.menu_id,
          activeItem: { id: contacts?.menu_item_id },
        }}
      >
        <Content ref={ref}>
          <Content.Header details={{ handlePrint }}>
            <Breadcrumb items={breadcrumbItems} />
          </Content.Header>
          <Content.Title>{contacts?.menu_item_title}</Content.Title>
          <Content.Body isShareable>
            <Table.ScrollContainer minWidth={500} pt="lg" type="native">
              <Table highlightOnHover striped horizontalSpacing="md" verticalSpacing="md" classNames={classnames}>
                <Table.Tbody>
                  {contacts?.data?.phone?.value ? (
                    <Table.Tr>
                      <Table.Td>
                        <h3>{contacts?.data?.phone?.label}</h3>
                      </Table.Td>
                      <Table.Td>
                        <Flex gap="sm">
                          {contacts?.data?.phone?.value
                            ?.trim()
                            ?.split(/[,;]/g)
                            ?.filter(Boolean)
                            ?.map((phone) => (
                              <CustomLink key={phone} href={`tel:${phone}`}>
                                {phone};
                              </CustomLink>
                            ))}
                        </Flex>
                      </Table.Td>
                    </Table.Tr>
                  ) : null}
                  {contacts?.data?.helpline?.value ? (
                    <Table.Tr>
                      <Table.Td>
                        <h3>{contacts?.data?.helpline?.label}</h3>
                      </Table.Td>
                      <Table.Td>
                        {contacts?.data?.helpline?.value?.split(/[,;]/g)?.map((phone) => (
                          <CustomLink key={phone} href={`tel:${phone?.trim()}`}>
                            {phone?.trim()};
                          </CustomLink>
                        ))}
                      </Table.Td>
                    </Table.Tr>
                  ) : null}
                  {contacts?.data?.website?.value ? (
                    <Table.Tr>
                      <Table.Td>
                        <h3>{contacts?.data?.website?.label}</h3>
                      </Table.Td>
                      <Table.Td>
                        <Flex align="center" gap="sm">
                          <CustomLink href={websiteLink || '/'} target="_blank">
                            {contacts?.data?.website?.value}
                          </CustomLink>
                        </Flex>
                      </Table.Td>
                    </Table.Tr>
                  ) : null}
                  {contacts?.data?.email?.value ? (
                    <Table.Tr>
                      <Table.Td>
                        <h3>{contacts?.data?.email?.label}</h3>
                      </Table.Td>
                      <Table.Td>
                        <CustomLink href={`mailto:${contacts?.data?.email?.value}`}>
                          {contacts?.data?.email?.value}
                        </CustomLink>
                      </Table.Td>
                    </Table.Tr>
                  ) : null}
                  {contacts?.data?.social_networks?.value && contacts?.data?.social_networks?.value?.length > 0 ? (
                    <Table.Tr>
                      <Table.Td>
                        <h3>{contacts?.data?.social_networks?.label}</h3>
                      </Table.Td>
                      <Table.Td>
                        <Flex gap="sm" align="center">
                          {contacts?.data?.social_networks?.value?.map((social) => (
                            <SocialMediaIcon
                              key={social?.link}
                              type={social?.type?.toLocaleLowerCase() as SocialMedia}
                              url={social?.link}
                              circle
                            />
                          ))}
                        </Flex>
                      </Table.Td>
                    </Table.Tr>
                  ) : null}
                  {contacts?.data?.address?.value ? (
                    <Table.Tr>
                      <Table.Td>
                        <h3>{contacts?.data?.address?.label}</h3>
                      </Table.Td>
                      <Table.Td>
                        <h3>{contacts?.data?.address?.value}</h3>
                      </Table.Td>
                    </Table.Tr>
                  ) : null}
                  {contacts?.data?.transport?.value ? (
                    <Table.Tr>
                      <Table.Td>
                        <h3>{contacts?.data?.transport?.label}</h3>
                      </Table.Td>
                      <Table.Td>
                        <h3 dangerouslySetInnerHTML={{ __html: sanitize(contacts?.data?.transport?.value || '') }}></h3>
                      </Table.Td>
                    </Table.Tr>
                  ) : null}
                  {contacts?.data?.working_mode?.value ? (
                    <Table.Tr>
                      <Table.Td>
                        <h3>{contacts?.data?.working_mode?.label}</h3>
                      </Table.Td>
                      <Table.Td>
                        <h3
                          dangerouslySetInnerHTML={{ __html: sanitize(contacts?.data?.working_mode?.value || '') }}
                        ></h3>
                      </Table.Td>
                    </Table.Tr>
                  ) : null}
                  {contacts?.data?.map?.value ? (
                    <Table.Tr>
                      <Table.Td className={classnames.td__map}>
                        <h3>{contacts?.data?.map?.label}</h3>
                      </Table.Td>
                      <Table.Td>
                        {contacts?.data?.map?.value ? (
                          <YMaps>
                            <Map
                              width="100%"
                              height="400px"
                              state={{
                                center: [
                                  parseFloat(contacts?.data?.map?.value?.latitude),
                                  parseFloat(contacts?.data?.map?.value?.longitude),
                                ],
                                zoom: parseInt(contacts?.data?.map?.value?.zoom),
                              }}
                            >
                              <Placemark
                                geometry={[contacts?.data?.map?.value?.latitude, contacts?.data?.map?.value?.longitude]}
                              />
                            </Map>
                          </YMaps>
                        ) : null}
                      </Table.Td>
                    </Table.Tr>
                  ) : null}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            <Group grow py="lg">
              {contacts?.data?.tin?.value ? (
                <>
                  <Button
                    component={CustomLink}
                    href={`https://murojaat.gov.uz/${locale}?organization_tin=${contacts?.data?.tin?.value}`}
                    target="_blank"
                    size="lg"
                    radius="0"
                    className={clsx(classnames.tin__btn, classnames.tin__primary)}
                    aria-label={t('Murojaat yuborish')}
                  >
                    {t('Murojaat yuborish')}
                  </Button>
                  {/* <Button
                    component={CustomLink}
                    href={`https://oldmy.gov.uz/${locale}/all-services?org_tin=${contacts?.data?.tin?.value}`}
                    target="_blank"
                    size="lg"
                    radius="0"
                    className={clsx(classnames.tin__btn, classnames.tin__secondary)}
                    aria-label={t('Interaktiv xizmatlar')}
                  >
                    {t('Interaktiv xizmatlar')}
                  </Button> */}
                </>
              ) : null}
            </Group>
          </Content.Body>
        </Content>
      </Section>
    </Container>
  );
}
